import { Close } from "@mui/icons-material";
import { Modal, Box, IconButton } from "@mui/material";
import { Map, Bot, ShieldCheck, FileText, AlertTriangle } from "lucide-react";
import PropTypes from "prop-types";

function TermsModal({ open, handleClose }) {
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="tnc-modal-title"
      aria-describedby="tnc-modal-description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "90%",
          maxWidth: "900px",
          maxHeight: "90vh",
          bgcolor: "#0e1f30",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "16px",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
          outline: "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 10,
            bgcolor: "rgba(255,255,255,0.07)",
            color: "#94a3b8",
            "&:hover": { bgcolor: "rgba(255,255,255,0.13)", color: "#f1f5f9" },
          }}
        >
          <Close />
        </IconButton>

        {/* Scrollable Content */}
        <Box
          sx={{
            overflowY: "auto",
            p: { xs: 3, sm: 4, md: 6 },
            "&::-webkit-scrollbar": { width: "5px" },
            "&::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.03)" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255,255,255,0.14)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": { background: "rgba(255,255,255,0.24)" },
          }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "80px",
                height: "80px",
                backgroundColor: "rgba(14,165,233,0.12)",
                border: "1px solid rgba(14,165,233,0.25)",
                borderRadius: "50%",
                marginBottom: "24px",
              }}
            >
              <FileText style={{ width: "40px", height: "40px", color: "#38bdf8" }} />
            </div>
            <h1
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "#f1f5f9",
                marginBottom: "16px",
                marginTop: 0,
                letterSpacing: "-0.02em",
              }}
            >
              BOTRIQ – Terms and Conditions
            </h1>
            <p
              style={{
                fontSize: "16px",
                color: "#94a3b8",
                maxWidth: "600px",
                margin: "0 auto",
                lineHeight: "1.65",
              }}
            >
              Please review these terms carefully before using Botriq’s robot control, mapping, and
              venue management platform.
            </p>
            <div
              style={{
                marginTop: "16px",
                display: "inline-block",
                fontSize: "13px",
                color: "#c2c8d3",
                background: "rgba(255,255,255,0.05)",
                padding: "4px 14px",
                borderRadius: "20px",
              }}
            >
              Last updated: January 1, 2025
            </div>
          </div>

          {/* Section 1 */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(14,165,233,0.12)",
                  border: "1px solid rgba(14,165,233,0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "12px",
                }}
              >
                <span style={{ color: "#38bdf8", fontWeight: "600" }}>1</span>
              </div>
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#e2e8f0",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Acceptance of Terms
              </h2>
            </div>
            <p style={{ color: "#94a3b8", lineHeight: "1.75" }}>
              By using Botriq (“the Platform”), you agree to comply with these Terms and Conditions.
              If you do not agree, you must discontinue use immediately.
            </p>
          </div>

          {/* Section 2 */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(16,185,129,0.12)",
                  border: "1px solid rgba(16,185,129,0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "12px",
                }}
              >
                <Bot style={{ width: "20px", height: "20px", color: "#34d399" }} />
              </div>
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#e2e8f0",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Robot Operations
              </h2>
            </div>
            <p style={{ color: "#94a3b8", lineHeight: "1.75", marginBottom: "16px" }}>
              Botriq enables control, monitoring, and navigation of autonomous robots or AGVs across
              mapped properties. You agree that:
            </p>
            <ul style={{ listStyle: "none", padding: 0, color: "#94a3b8" }}>
              <li style={{ display: "flex", marginBottom: "8px" }}>
                <span style={{ color: "#38bdf8", marginRight: "12px" }}>›</span>
                <span>
                  All deployed robots operate under your legal authority and safety protocols.
                </span>
              </li>
              <li style={{ display: "flex", marginBottom: "8px" }}>
                <span style={{ color: "#38bdf8", marginRight: "12px" }}>›</span>
                <span>
                  You ensure compliance with applicable industrial and workplace safety laws.
                </span>
              </li>
              <li style={{ display: "flex", marginBottom: "8px" }}>
                <span style={{ color: "#38bdf8", marginRight: "12px" }}>›</span>
                <span>
                  Robots are not used for unauthorized surveillance, harm, or data misuse.
                </span>
              </li>
            </ul>
          </div>

          {/* Section 3 */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(139,92,246,0.12)",
                  border: "1px solid rgba(139,92,246,0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "12px",
                }}
              >
                <Map style={{ width: "20px", height: "20px", color: "#a78bfa" }} />
              </div>
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#e2e8f0",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Map and Venue Data
              </h2>
            </div>
            <p style={{ color: "#94a3b8", lineHeight: "1.75" }}>
              Botriq uses map-based navigation and venue layouts for autonomous operations. You
              retain ownership of uploaded map and venue data, while granting Botriq permission to
              process it for operational purposes.
            </p>
          </div>

          {/* Section 4 */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(14,165,233,0.12)",
                  border: "1px solid rgba(14,165,233,0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "12px",
                }}
              >
                <ShieldCheck style={{ width: "20px", height: "20px", color: "#38bdf8" }} />
              </div>
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#e2e8f0",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Data Security and Privacy
              </h2>
            </div>
            <p style={{ color: "#94a3b8", lineHeight: "1.75" }}>
              All data transmitted through Botriq — including maps, robot telemetry, and user
              credentials — is protected using industry-grade encryption. We do not sell or share
              your data without consent.
            </p>
          </div>

          {/* Section 5 */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(239,68,68,0.12)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "12px",
                }}
              >
                <AlertTriangle style={{ width: "20px", height: "20px", color: "#f87171" }} />
              </div>
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#e2e8f0",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Service Limitations
              </h2>
            </div>
            <div
              style={{
                backgroundColor: "rgba(245,158,11,0.08)",
                borderLeft: "3px solid #f59e0b",
                padding: "18px 20px",
                borderRadius: "0 8px 8px 0",
              }}
            >
              <p
                style={{
                  color: "#fcd34d",
                  marginBottom: "16px",
                  marginTop: 0,
                  lineHeight: "1.7",
                  fontSize: "14px",
                }}
              >
                <strong>Notice:</strong> Botriq is provided “as is.” We do not guarantee
                uninterrupted service, error-free robot operation, or compatibility with all
                hardware systems.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "12px",
                }}
              >
                <span style={{ color: "#64748b", fontWeight: "600" }}>∞</span>
              </div>
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#e2e8f0",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Modifications and Termination
              </h2>
            </div>
            <p style={{ color: "#94a3b8", lineHeight: "1.75" }}>
              We may update these Terms periodically. Continued use after updates implies
              acceptance. Botriq may suspend or terminate accounts found in violation of operational
              or safety policies.
            </p>
          </div>

          {/* Contact Section */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "32px" }}>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#e2e8f0",
                marginBottom: "16px",
                letterSpacing: "-0.01em",
              }}
            >
              Contact Information
            </h2>
            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "10px",
                padding: "20px 24px",
              }}
            >
              <div style={{ color: "#64748b", fontSize: "14px" }}>
                <p style={{ margin: "4px 0" }}>
                  <strong style={{ color: "#e2e8f0" }}>Botriq Innovations Pvt Ltd</strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  Address: 304 MPSEDC New IT Park, Electronic Complex, Indore
                </p>
                <p style={{ margin: "8px 0 4px 0" }}>
                  Website:{" "}
                  <a
                    href="https://botriq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#38bdf8", textDecoration: "none", fontWeight: "500" }}
                  >
                    botriq.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </Box>
      </Box>
    </Modal>
  );
}

TermsModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default TermsModal;
